import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';

export type RecipeBeneficenciaFieldKey =
  | 'patientName'
  | 'age'
  | 'weight'
  | 'weightPercentile'
  | 'height'
  | 'heightPercentile'
  | 'prescription'
  | 'doctorName'
  | 'consultDate'
  | 'qrCode';

export interface FieldPosition {
  top: number;
  left: number;
  width?: number;
}

export interface RecipeBeneficenciaCanvasProps {
  patientName: string;
  ageYears: string;
  ageMonths: string;
  ageDays?: string;
  weight: string;
  weightPercentile: string;
  height: string;
  heightPercentile: string;
  prescription: string;
  doctorName: string;
  consultDate: string;
  recetaToken?: string | null;
  className?: string;
  editMode?: boolean;
  selectedField?: RecipeBeneficenciaFieldKey | null;
  onSelectField?: (field: RecipeBeneficenciaFieldKey) => void;
  fieldPositions?: Partial<Record<RecipeBeneficenciaFieldKey, FieldPosition>>;
  onFieldBoundsChange?: (field: RecipeBeneficenciaFieldKey, size: { width: number; height: number }) => void;
}

export const DEFAULT_BENEFICENCIA_POSITIONS: Record<RecipeBeneficenciaFieldKey, FieldPosition> = {
  patientName: { top: 275, left: 124 },
  age: { top: 269, left: 1000, width: 350 },
  weight: { top: 338, left: 163 },
  weightPercentile: { top: 330, left: 100 },
  height: { top: 310, left: 1000, width: 350 },
  heightPercentile: { top: 450, left: 1000, width: 350 },
  prescription: { top: 495, left: 140 },
  doctorName: { top: 1588, left: 112 },
  consultDate: { top: 1597, left: 1000, width: 350 },
  qrCode: { top: 1580, left: 1450 },
};

export const RECIPE_BENEFICENCIA_FIELD_KEYS = Object.keys(DEFAULT_BENEFICENCIA_POSITIONS) as RecipeBeneficenciaFieldKey[];

const RecipeBeneficenciaCanvas: React.FC<RecipeBeneficenciaCanvasProps> = ({
  patientName,
  ageYears,
  ageMonths,
  ageDays,
  weight,
  weightPercentile,
  height,
  heightPercentile,
  prescription,
  doctorName,
  consultDate,
  recetaToken,
  className = '',
  editMode = false,
  selectedField,
  onSelectField,
  fieldPositions,
  onFieldBoundsChange,
}) => {
  const mergedPositions = useMemo(() => {
    return RECIPE_BENEFICENCIA_FIELD_KEYS.reduce<Record<RecipeBeneficenciaFieldKey, FieldPosition>>((acc, key) => {
      const base = DEFAULT_BENEFICENCIA_POSITIONS[key];
      const override = fieldPositions?.[key];
      acc[key] = override ? { ...base, ...override } : { ...base };
      return acc;
    }, {} as Record<RecipeBeneficenciaFieldKey, FieldPosition>);
  }, [fieldPositions]);

  const handleSelect = (field: RecipeBeneficenciaFieldKey) => {
    if (editMode) {
      onSelectField?.(field);
    }
  };

  const refs = useMemo(() => {
    return RECIPE_BENEFICENCIA_FIELD_KEYS.reduce<Record<RecipeBeneficenciaFieldKey, React.RefObject<HTMLDivElement>>>((acc, key) => {
      acc[key] = React.createRef<HTMLDivElement>();
      return acc;
    }, {} as Record<RecipeBeneficenciaFieldKey, React.RefObject<HTMLDivElement>>);
  }, []);

  useLayoutEffect(() => {
    if (!onFieldBoundsChange) {
      return;
    }

    (Object.keys(refs) as RecipeBeneficenciaFieldKey[]).forEach((key) => {
      const el = refs[key].current;
      if (el) {
        const rect = el.getBoundingClientRect();
        onFieldBoundsChange(key, { width: rect.width, height: rect.height });
      }
    });
  }, [patientName, ageYears, ageMonths, ageDays, weight, weightPercentile, height, heightPercentile, prescription, doctorName, consultDate, editMode, refs, onFieldBoundsChange]);

  useEffect(() => {
    if (!onFieldBoundsChange) {
      return;
    }

    const observers: ResizeObserver[] = [];

    (Object.keys(refs) as RecipeBeneficenciaFieldKey[]).forEach((key) => {
      const el = refs[key].current;
      if (!el) {
        return;
      }

      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          onFieldBoundsChange(key, { width, height });
        }
      });

      observer.observe(el);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [refs, onFieldBoundsChange]);

  const classSuffix: Record<RecipeBeneficenciaFieldKey, string> = {
    patientName: 'patient-name',
    age: 'age',
    weight: 'weight',
    weightPercentile: 'weight-percentile',
    height: 'height',
    heightPercentile: 'height-percentile',
    prescription: 'prescription',
    doctorName: 'doctor-name',
    consultDate: 'consult-date',
    qrCode: 'qr-code',
  };

  const styleFor = (field: RecipeBeneficenciaFieldKey): React.CSSProperties => {
    const pos = mergedPositions[field];
    const base: React.CSSProperties = {
      top: `${pos.top / 100}cm`,
      left: `${pos.left / 100}cm`,
    };
    if (pos.width != null) {
      base.width = `${pos.width / 100}cm`;
    }
    if (editMode) {
      base.cursor = 'move';
    }
    return base;
  };

  const focusableProps = (field: RecipeBeneficenciaFieldKey) => {
    const baseClass = `recipe-field recipe-beneficencia-field-${classSuffix[field]}`;

    if (editMode) {
      return {
        tabIndex: 0,
        onClick: () => handleSelect(field),
        onFocus: () => handleSelect(field),
        className: `${baseClass} ${selectedField === field ? 'recipe-field-selected' : ''}`.trim(),
      };
    }

    return {
      className: baseClass,
    };
  };

  // Formatear edad
  const ageText = ageDays != null && ageDays !== ''
    ? `${ageYears} años ${ageMonths} meses ${ageDays} días`
    : `${ageYears} años ${ageMonths} meses`;

  return (
    <div
      className={`recipe-print-page recipe-beneficencia ${editMode ? 'recipe-print-page--edit' : ''} ${className}`.trim()}
    >
      <div ref={refs.patientName} style={styleFor('patientName')} {...focusableProps('patientName')}>
        {patientName}
      </div>
      <div ref={refs.age} style={styleFor('age')} {...focusableProps('age')}>
        {ageText}
      </div>
      <div ref={refs.weight} style={styleFor('weight')} {...focusableProps('weight')}>
        {weight} kg
        {weightPercentile && (
          <div style={{ display: 'block', marginTop: '0.15cm', fontSize: '8pt', fontWeight: 700 }}>
            {weightPercentile}
          </div>
        )}
      </div>
      <div ref={refs.height} style={styleFor('height')} {...focusableProps('height')}>
        {height} cm
        {heightPercentile && (
          <div style={{ display: 'block', marginTop: '0.15cm', fontSize: '8pt', fontWeight: 700 }}>
            {heightPercentile}
          </div>
        )}
      </div>
      <div ref={refs.prescription} style={styleFor('prescription')} {...focusableProps('prescription')}>
        <span className="whitespace-pre-wrap leading-relaxed" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', tabSize: 38 }}>{prescription}</span>
      </div>
      <div ref={refs.doctorName} style={{ ...styleFor('doctorName'), fontSize: '11pt' }} {...focusableProps('doctorName')}>
        {doctorName}
      </div>
      <div ref={refs.consultDate} style={{ ...styleFor('consultDate'), fontSize: '11pt' }} {...focusableProps('consultDate')}>
        {consultDate}
      </div>

      {/* Código QR */}
      {recetaToken && (
        <div ref={refs.qrCode} style={styleFor('qrCode')} {...focusableProps('qrCode')}>
          <QRCodeSVG
            value={`${window.location.origin}/receta/${recetaToken}`}
            size={45}
            level="M"
            includeMargin={false}
          />
        </div>
      )}
    </div>
  );
};

export default RecipeBeneficenciaCanvas;
